import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Container,
  Paper,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  LinearProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  card: {
    padding: theme.spacing(2),
    height: "100%"
  },
  cardLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 4
  },
  cardValue: {
    fontSize: 24,
    fontWeight: 600,
    color: "#1a1a1a"
  },
  cardSub: {
    fontSize: 11,
    color: "#888",
    marginTop: 4
  },
  adminChip: {
    background: "#ffecb3",
    color: "#5d4037",
    fontWeight: 600
  }
}));

// Mapeamento de feature → label PT-BR pra UI
const FEATURE_LABELS = {
  sales_ai: "IA de Vendas (objeção)",
  message_review: "Revisão de mensagem",
  transcription: "Transcrição de áudio",
  ai_flow_response: "Fluxo: resposta IA",
  ai_flow_classify: "Fluxo: classificar",
  ai_flow_extract: "Fluxo: extrair dados",
  ai_flow_summarize: "Fluxo: resumir conversa"
};

const featureLabel = key => FEATURE_LABELS[key] || key;

const formatMoney = (v, currency = "BRL") => {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const PERIOD_OPTIONS = [
  { key: "7d", label: "Últimos 7 dias", days: 7 },
  { key: "30d", label: "Últimos 30 dias", days: 30 },
  { key: "90d", label: "Últimos 90 dias", days: 90 },
  { key: "all", label: "Tudo", days: null }
];

const AiConsumption = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin =
    user?.profile === "admin" ||
    user?.profile === "owner" ||
    user?.profile === "manager";

  const [period, setPeriod] = useState("30d");
  const [showAdmin, setShowAdmin] = useState(isAdmin);
  const [summary, setSummary] = useState(null);
  const [byFeature, setByFeature] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [details, setDetails] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);

  const [spendCap, setSpendCap] = useState(null);
  const [capDialogOpen, setCapDialogOpen] = useState(false);
  const [capForm, setCapForm] = useState({ dailyLimit: 0, monthlyLimit: 0 });
  const [savingCap, setSavingCap] = useState(false);

  const loadSpendCap = async () => {
    try {
      const { data } = await api.get("/ai-spend-cap");
      setSpendCap(data);
      setCapForm({ dailyLimit: data.dailyLimit, monthlyLimit: data.monthlyLimit });
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (isAdmin) loadSpendCap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const saveSpendCap = async () => {
    setSavingCap(true);
    try {
      const { data } = await api.put("/ai-spend-cap", {
        dailyLimit: Number(capForm.dailyLimit) || 0,
        monthlyLimit: Number(capForm.monthlyLimit) || 0
      });
      setSpendCap(data);
      setCapDialogOpen(false);
      toast.success("Teto de gasto de IA atualizado.");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingCap(false);
    }
  };

  const range = useMemo(() => {
    const opt = PERIOD_OPTIONS.find(p => p.key === period);
    if (!opt || !opt.days) return {};
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - opt.days);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [period]);

  const scope = showAdmin && isAdmin ? "admin" : "client";

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const params = { ...range, scope };
        const [s, f, u, d] = await Promise.all([
          api.get("/ai-assist/consumption/summary", { params }),
          api.get("/ai-assist/consumption/by-feature", { params }),
          api.get("/ai-assist/consumption/by-user", { params }),
          api.get("/ai-assist/consumption/details", {
            params: { ...params, pageNumber: 1 }
          })
        ]);
        if (!active) return;
        setSummary(s.data);
        setByFeature(f.data.items || []);
        setByUser(u.data.items || []);
        setDetails(d.data);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [range, scope]);

  const isAdminView = summary?.scope === "admin";

  return (
    <Container className={classes.root} maxWidth="lg">
      <div className={classes.header}>
        <MemoryOutlinedIcon style={{ color: "#6a1b9a", fontSize: 28 }} />
        <Typography variant="h5">Consumo de IA</Typography>
        {isAdminView && (
          <Chip label="Visão admin" size="small" className={classes.adminChip} />
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <TextField
            select
            label="Período"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 180 }}
          >
            {PERIOD_OPTIONS.map(p => (
              <MenuItem key={p.key} value={p.key}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          {isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={showAdmin}
                  onChange={e => setShowAdmin(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Custo real"
            />
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 64 }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {isAdminView && spendCap && (
            <Paper variant="outlined" className={classes.card} style={{ marginBottom: 16 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Teto de gasto de IA
                </Typography>
                <IconButton size="small" onClick={() => setCapDialogOpen(true)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Quando o gasto do período atinge o teto, novas chamadas de IA
                (fluxo, sugestão de venda, revisão, transcrição) param
                automaticamente até o próximo período — o que já saiu continua
                registrado normalmente.
              </Typography>
              <Grid container spacing={2} style={{ marginTop: 4 }}>
                {[
                  { label: "Hoje", spent: spendCap.dailySpent, limit: spendCap.dailyLimit },
                  { label: "Este mês", spent: spendCap.monthlySpent, limit: spendCap.monthlyLimit }
                ].map(row => {
                  const pct = row.limit > 0 ? Math.min(100, (row.spent / row.limit) * 100) : 0;
                  const estourado = row.limit > 0 && row.spent >= row.limit;
                  return (
                    <Grid item xs={12} sm={6} key={row.label}>
                      <Typography variant="body2">
                        {row.label}: {formatMoney(row.spent)}
                        {row.limit > 0 ? ` de ${formatMoney(row.limit)}` : " — sem teto"}
                      </Typography>
                      {row.limit > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          style={{
                            marginTop: 4,
                            height: 8,
                            borderRadius: 4
                          }}
                          color={estourado ? "secondary" : "primary"}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          )}

          {/* Cards principais — sempre visíveis */}
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" className={classes.card}>
                <div className={classes.cardLabel}>Chamadas de IA</div>
                <div className={classes.cardValue}>
                  {summary?.totalCalls || 0}
                </div>
                <div className={classes.cardSub}>no período</div>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" className={classes.card}>
                <div className={classes.cardLabel}>Valor de uso de IA</div>
                <div className={classes.cardValue} style={{ color: "#1976d2" }}>
                  {formatMoney(summary?.totalSellPrice, summary?.currency)}
                </div>
                <div className={classes.cardSub}>
                  {isAdminView
                    ? "preço de venda configurado"
                    : "Atualmente incluso no seu plano"}
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" className={classes.card}>
                <div className={classes.cardLabel}>Áudios transcritos</div>
                <div className={classes.cardValue}>
                  {summary?.totalTranscriptions || 0}
                </div>
                <div className={classes.cardSub}>via Whisper</div>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" className={classes.card}>
                <div className={classes.cardLabel}>Mensagens revisadas</div>
                <div className={classes.cardValue}>
                  {summary?.totalReviews || 0}
                </div>
                <div className={classes.cardSub}>com IA</div>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Paper variant="outlined" className={classes.card}>
                <div className={classes.cardLabel}>Usuário que mais usou IA</div>
                <div className={classes.cardValue} style={{ fontSize: 18 }}>
                  {summary?.topUser?.name || "—"}
                </div>
                <div className={classes.cardSub}>
                  {summary?.topUser?.calls || 0} chamadas
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper variant="outlined" className={classes.card}>
                <div className={classes.cardLabel}>Funcionalidade mais usada</div>
                <div className={classes.cardValue} style={{ fontSize: 18 }}>
                  {featureLabel(summary?.topFeature?.feature) || "—"}
                </div>
                <div className={classes.cardSub}>
                  {summary?.topFeature?.calls || 0} execuções
                </div>
              </Paper>
            </Grid>
            {isAdminView && (
              <Grid item xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  className={classes.card}
                  style={{ background: "#fff8e1" }}
                >
                  <div className={classes.cardLabel}>Modelo mais utilizado</div>
                  <div className={classes.cardValue} style={{ fontSize: 18 }}>
                    {summary?.topModel?.model || "—"}
                  </div>
                  <div className={classes.cardSub}>
                    {summary?.topModel?.calls || 0} chamadas
                  </div>
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* Cards admin only */}
          {isAdminView && (
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  className={classes.card}
                  style={{ background: "#ffebee" }}
                >
                  <div className={classes.cardLabel}>Custo real</div>
                  <div
                    className={classes.cardValue}
                    style={{ color: "#c62828" }}
                  >
                    {formatMoney(summary?.totalRealCost, summary?.currency)}
                  </div>
                  <div className={classes.cardSub}>pago ao provedor</div>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  className={classes.card}
                  style={{ background: "#e8f5e9" }}
                >
                  <div className={classes.cardLabel}>Lucro estimado</div>
                  <div
                    className={classes.cardValue}
                    style={{ color: "#2e7d32" }}
                  >
                    {formatMoney(summary?.totalProfit, summary?.currency)}
                  </div>
                  <div className={classes.cardSub}>
                    margem{" "}
                    {summary?.marginPercent != null
                      ? `${summary.marginPercent}%`
                      : "—"}
                  </div>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper variant="outlined" className={classes.card}>
                  <div className={classes.cardLabel}>Total de tokens</div>
                  <div className={classes.cardValue} style={{ fontSize: 22 }}>
                    {(summary?.totalTokens || 0).toLocaleString("pt-BR")}
                  </div>
                  <div className={classes.cardSub}>input + output</div>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Por funcionalidade + por usuário */}
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.card}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                  Por funcionalidade
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Função</TableCell>
                      <TableCell align="right">Chamadas</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      {isAdminView && (
                        <TableCell align="right">Lucro</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byFeature.map(row => (
                      <TableRow key={row.feature}>
                        <TableCell>{featureLabel(row.feature)}</TableCell>
                        <TableCell align="right">{row.calls}</TableCell>
                        <TableCell align="right">
                          {formatMoney(row.sellPrice)}
                        </TableCell>
                        {isAdminView && (
                          <TableCell align="right" style={{ color: "#2e7d32" }}>
                            {formatMoney(row.profit)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {byFeature.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isAdminView ? 4 : 3} align="center">
                          Sem dados.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.card}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                  Por usuário (top 50)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell align="right">Chamadas</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      {isAdminView && (
                        <TableCell align="right">Lucro</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byUser.map(row => (
                      <TableRow key={row.userId}>
                        <TableCell>{row.name || `User #${row.userId}`}</TableCell>
                        <TableCell align="right">{row.calls}</TableCell>
                        <TableCell align="right">
                          {formatMoney(row.sellPrice)}
                        </TableCell>
                        {isAdminView && (
                          <TableCell align="right" style={{ color: "#2e7d32" }}>
                            {formatMoney(row.profit)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {byUser.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isAdminView ? 4 : 3} align="center">
                          Sem dados.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>

          {/* Detalhes — últimas chamadas */}
          <Paper variant="outlined" className={classes.card}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
              Últimas chamadas ({details.count} no total)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Função</TableCell>
                    <TableCell>Usuário</TableCell>
                    <TableCell>Ticket</TableCell>
                    <TableCell>Modelo</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    {isAdminView && (
                      <>
                        <TableCell align="right">Custo</TableCell>
                        <TableCell align="right">Lucro</TableCell>
                      </>
                    )}
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.items.map(row => (
                    <TableRow key={row.id}>
                      <TableCell style={{ fontSize: 11 }}>
                        {new Date(row.createdAt).toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell>{featureLabel(row.feature)}</TableCell>
                      <TableCell>{row.User?.name || "—"}</TableCell>
                      <TableCell>{row.ticketId || "—"}</TableCell>
                      <TableCell style={{ fontSize: 11 }}>{row.model}</TableCell>
                      <TableCell align="right">
                        {(row.inputTokens || 0) + (row.outputTokens || 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(row.estimatedPriceSale)}
                      </TableCell>
                      {isAdminView && (
                        <>
                          <TableCell align="right" style={{ color: "#c62828" }}>
                            {formatMoney(row.estimatedCostReal)}
                          </TableCell>
                          <TableCell align="right" style={{ color: "#2e7d32" }}>
                            {formatMoney(row.estimatedProfit)}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          style={{
                            background:
                              row.status === "ok" ? "#e8f5e9" : "#ffebee",
                            color: row.status === "ok" ? "#1b5e20" : "#b71c1c"
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {details.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdminView ? 10 : 8} align="center">
                        Sem chamadas no período.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <Dialog open={capDialogOpen} onClose={() => setCapDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Teto de gasto de IA</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary">
            Zero significa sem teto naquele período.
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            type="number"
            label="Teto diário (R$)"
            style={{ marginTop: 16 }}
            value={capForm.dailyLimit}
            onChange={e => setCapForm(f => ({ ...f, dailyLimit: e.target.value }))}
          />
          <TextField
            fullWidth
            variant="outlined"
            type="number"
            label="Teto mensal (R$)"
            style={{ marginTop: 16 }}
            value={capForm.monthlyLimit}
            onChange={e => setCapForm(f => ({ ...f, monthlyLimit: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCapDialogOpen(false)} disabled={savingCap}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={saveSpendCap}
            disabled={savingCap}
          >
            {savingCap ? "Salvando…" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AiConsumption;
